import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Zap, MoreHorizontal, Save, Repeat } from "lucide-react";
import { fmtBRL } from "@/lib/api";
import { DespesaRow } from "@/components/financeiro/DespesaRow";
import { RecorrenciasCard } from "@/components/financeiro/RecorrenciasCard";

/**
 * Painel de Rateio + Despesas do mês.
 * Gerencia UI de rateio dinâmico (Luz/Outras) e listagem/exclusão de despesas.
 */
export function RateioPanel({
  rateio, rateioForm, setRateioForm, totalRateioDinamico, savingRateio, onSalvarRateio,
  modalOpen, setModalOpen, novaDespesa, setNovaDespesa, onAddDespesa, onDelDespesa, onChanged,
}) {
  return (
    <div className="space-y-6">
      {rateio && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Fixas</div>
            <div className="stat-num text-2xl mt-2">{fmtBRL(rateio.despesas_fixas_total)}</div>
          </div>
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Variáveis</div>
            <div className="stat-num text-2xl mt-2">{fmtBRL(rateio.despesas_variaveis_total)}</div>
          </div>
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Cota de rateio</div>
            <div className="stat-num text-2xl mt-2 text-info">{fmtBRL(rateio.cota_rateio)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">÷ {rateio.unidades_ocupadas} ocupadas</div>
          </div>
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">IPTU / estande</div>
            <div className="stat-num text-2xl mt-2 text-warning">{fmtBRL(rateio.iptu_fracao_por_estande)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">parcela ÷ {rateio.total_estandes || 30}</div>
          </div>
        </div>
      )}

      <Card className="p-6 rounded-sm border-2 border-info/40 bg-info/5" data-testid="rateio-dinamico-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label-tag text-info">Lançamento mensal de rateio</div>
            <h2 className="font-heading font-bold text-xl mt-1">Despesas dinâmicas do mês</h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-lg leading-relaxed">
              Informe o valor total apurado da competência para cada tipo de despesa
              rateável. Ao salvar, o motor híbrido divide o total pelas
              <b> {rateio?.unidades_ocupadas || 0}</b> unidades ocupadas e recompoe a mensalidade
              (aluguel base + IPTU + cota de rateio + taxa de água individual).
            </p>
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              💧 A <b>Taxa de Água</b> não entra mais no rateio: é cobrada individualmente por contrato conforme a atividade do lojista.
            </p>
          </div>
          <div className="text-right">
            <div className="label-tag">Total do rateio</div>
            <div className="stat-num text-2xl text-info">{fmtBRL(totalRateioDinamico)}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="label-tag flex items-center gap-1"><Zap size={12} /> Luz comum (R$)</Label>
            <Input data-testid="rateio-luz" type="number" step="0.01" value={rateioForm.luz}
              onChange={(e) => setRateioForm({ ...rateioForm, luz: e.target.value })}
              placeholder="0,00" className="rounded-sm" />
          </div>
          <div>
            <Label className="label-tag flex items-center gap-1"><MoreHorizontal size={12} /> Outras despesas (R$)</Label>
            <Input data-testid="rateio-outras" type="number" step="0.01" value={rateioForm.outras}
              onChange={(e) => setRateioForm({ ...rateioForm, outras: e.target.value })}
              placeholder="0,00" className="rounded-sm" />
          </div>
        </div>
        <Button data-testid="rateio-salvar" onClick={onSalvarRateio} disabled={savingRateio}
          className="w-full mt-4 rounded-sm bg-info text-white hover:bg-info/90">
          <Save size={14} className="mr-2" />
          {savingRateio ? "Salvando..." : "Salvar rateio do mês"}
        </Button>
        {rateio?.unidades_ocupadas > 0 && totalRateioDinamico > 0 && (
          <div className="mt-3 text-xs text-center text-muted-foreground font-mono">
            Prévia: {fmtBRL(totalRateioDinamico / rateio.unidades_ocupadas)} por unidade ocupada
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-sm border-border">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-xl">Despesas do mês</h2>
          <Dialog open={modalOpen} onOpenChange={setModalOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nova-despesa" size="sm" className="rounded-sm bg-foreground text-background hover:bg-foreground/90">
                <Plus size={14} className="mr-1" /> Nova
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Nova despesa</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-tag">Descrição</Label>
                  <Input data-testid="dnew-desc" value={novaDespesa.descricao}
                    onChange={(e) => setNovaDespesa({ ...novaDespesa, descricao: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-tag">Categoria</Label>
                    <Select value={novaDespesa.categoria} onValueChange={(v) => setNovaDespesa({ ...novaDespesa, categoria: v })}>
                      <SelectTrigger data-testid="dnew-cat"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FIXA">Fixa</SelectItem>
                        <SelectItem value="VARIAVEL">Variável</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-tag">Valor (R$)</Label>
                    <Input data-testid="dnew-valor" type="number" step="0.01" value={novaDespesa.valor}
                      onChange={(e) => setNovaDespesa({ ...novaDespesa, valor: e.target.value })} />
                  </div>
                </div>
                <div className="flex items-center justify-between border border-border/60 rounded-sm p-3">
                  <div>
                    <Label htmlFor="dnew-recorrente" className="text-sm font-medium flex items-center gap-1.5">
                      <Repeat size={13} className="text-info" /> Recorrência mensal
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Gera esta conta automaticamente todo mês como Pendente.</p>
                  </div>
                  <Switch id="dnew-recorrente" data-testid="dnew-recorrente" checked={!!novaDespesa.recorrente}
                    onCheckedChange={(v) => setNovaDespesa({ ...novaDespesa, recorrente: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                <Button data-testid="dnew-salvar" onClick={onAddDespesa}
                  className="bg-foreground text-background hover:bg-foreground/90">Adicionar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-2">
          {rateio && [...(rateio.detalhes_fixas || []), ...(rateio.detalhes_variaveis || [])].map((d) => (
            <DespesaRow key={d.id} d={d} onDelete={onDelDespesa} onChanged={onChanged} />
          ))}
          {rateio && (rateio.detalhes_fixas?.length ?? 0) + (rateio.detalhes_variaveis?.length ?? 0) === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm border border-dashed rounded-sm">
              Nenhuma despesa lançada nesta competência.
            </div>
          )}
        </div>
      </Card>

      <RecorrenciasCard refreshKey={rateio} onChanged={onChanged} />
    </div>
  );
}
